import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertCircle, Calendar, Search, Download, RefreshCw } from 'lucide-react';
import { useApi } from '../../contexts/ApiContext';
import Sidebar from '../../components/Layout/Sidebar';
import Header from '../../components/Layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { StatsOverview } from '../../components/home';
import EventCard from '../../components/events/EventCard';

const dateFilters = [
  { value: 'today', label: 'Today' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
];

export default function EventDashboard() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();

  const dateFilter = searchParams.get('date') || 'today';
  const searchVal = searchParams.get('search') || '';
  const seriesFilter = searchParams.get('series') || '';
  const [search, setSearch] = useState(searchVal);
  const [prevSearchVal, setPrevSearchVal] = useState(searchVal);
  if (searchVal !== prevSearchVal) {
    setPrevSearchVal(searchVal);
    setSearch(searchVal);
  }

  const { data: meta = { stats: {}, series: [] }, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-meta'],
    queryFn: async () => {
      const result = await api.home.getDashboard();
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load dashboard');
      return { stats: result.data.stats || {}, series: result.data.series || [] };
    },
  });
  const stats = meta.stats;
  const seriesOptions = meta.series;
  const selectedSeriesSlug = seriesFilter
    ? seriesOptions.find((s) => String(s.id) === seriesFilter)?.slug
    : null;

  const { data: eventsData = { events: [], total: 0 }, isLoading: eventsLoading, error: eventsError, refetch } = useQuery({
    queryKey: ['dashboard-events', searchParams.toString()],
    queryFn: async () => {
      const params = { limit: 100, sort: 'event_date', order: dateFilter === 'past' ? 'desc' : 'asc', prioritize_wrg: 'true' };
      if (searchVal) params.search = searchVal;
      if (seriesFilter) params.series_id = seriesFilter;
      if (dateFilter === 'today') {
        params.today = 'true';
      } else if (dateFilter === 'upcoming') {
        params.upcoming = 'true';
      } else if (dateFilter === 'past') {
        params.past = 'true';
      }
      const result = await api.events.getAll(params);
      if (!result.success) throw new Error(result.message || 'Failed to fetch events');
      return {
        events: result.data.events || [],
        total: result.data.pagination?.total || 0
      };
    },
  });
  const events = eventsData.events;

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) params.set('search', search);
    else params.delete('search');
    setSearchParams(params);
  };

  const handleDateFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    params.set('date', value);
    setSearchParams(params);
  };

  const handleSeriesFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('series', value);
    else params.delete('series');
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        currentSeries={null}
      />

      <Header
        sidebarCollapsed={sidebarCollapsed}
        onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title="Dashboard"
        subtitle="Select an event to manage"
      />

      <main
        className={clsx(
          'pt-14 min-h-screen transition-all duration-200',
          sidebarCollapsed ? 'ml-14' : 'ml-56'
        )}
      >
        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            <div className="lg:col-span-3 space-y-4">
              <Card padding="compact">
                <CardContent className="p-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <form onSubmit={handleSearch} className="flex-1">
                      <Input
                        placeholder="Search events, tracks..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        leftIcon={Search}
                        size="sm"
                      />
                    </form>
                    <div className="flex items-center gap-1.5">
                      <Select
                        value={seriesFilter}
                        onChange={(e) => handleSeriesFilter(e.target.value)}
                        className="w-52"
                        size="sm"
                      >
                        <option value="">All series</option>
                        {seriesOptions.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </Select>
                      <Select
                        value={dateFilter}
                        onChange={(e) => handleDateFilter(e.target.value)}
                        className="w-40"
                        size="sm"
                      >
                        {dateFilters.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </Select>
                      <Button variant="ghost" size="xs" onClick={() => refetch()} disabled={eventsLoading}>
                        <RefreshCw className={`w-3.5 h-3.5 ${eventsLoading ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {eventsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} padding="compact" className="animate-pulse">
                      <CardContent className="p-2.5">
                        <div className="flex gap-3">
                          <div className="w-11 h-12 bg-slate-200 rounded" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-200 rounded w-1/3" />
                            <div className="h-3 bg-slate-200 rounded w-1/2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : eventsError ? (
                <Card padding="compact">
                  <CardContent className="p-8 text-center">
                    <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                    <h3 className="text-md font-medium text-slate-800 mb-1.5">Error Loading Events</h3>
                    <p className="text-xs text-slate-500 mb-3">{eventsError.message}</p>
                    <Button size="sm" onClick={() => refetch()}>
                      Try Again
                    </Button>
                  </CardContent>
                </Card>
              ) : events.length === 0 ? (
                <Card padding="compact">
                  <CardContent className="p-8 text-center">
                    <Calendar className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <h3 className="text-md font-medium text-slate-800 mb-1.5">No events found</h3>
                    <p className="text-xs text-slate-500">
                      {searchVal ? 'Try adjusting your search or date filter' : 'No events for this date range'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {events.map((event) => (
                    <EventCard key={event.id} event={event} seriesSlug={selectedSeriesSlug} highlightWrg showSeries />
                  ))}
                  {eventsData.total > events.length && (
                    <p className="text-xs text-slate-400 text-center pt-1">
                      Showing {events.length} of {eventsData.total} events. Refine the search or open a series event list for the full history.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-5">
              <StatsOverview stats={stats} loading={statsLoading} />

              <Card padding="compact">
                <CardHeader border={false} className="mb-3 pb-0">
                  <div className="flex items-center justify-between">
                    <CardTitle>Eloy Relay Client</CardTitle>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      v1.5.1
                    </span>
                  </div>
                </CardHeader>
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    Download the relay client for live timing integration.
                  </p>
                  <a
                    href="https://www.dropbox.com/scl/fi/osbe2o0tk8md1crdznw3b/Eloy_1.5.1_x64-setup.exe?rlkey=jpok0mr4evc23xwf5abjouosd&st=da8pzay6&dl=0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-wrg-blue-600 hover:bg-wrg-blue-700 text-white text-sm font-medium rounded transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download for Windows
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
